import { useEffect, useMemo, useState } from "react";
import DynamicForm from "@view/common/DynamicForm";
import { DynamicFormSchema } from "@view/common/DynamicForm/DynamicFormContracts";
import styles from "./LoginWithPasswordSlide.module.css";
import { loginWithEmailAndPassword, sendVerificationCode } from "@view/common/Api/LoginApi";
import LoginSubmitField from "./LoginSubmitFieldWithBack";
import { ISignInProcessInitWithUrlDto } from "@presentation/controllersModels/ISignInProcessInitWithUrlDto";
import { useLoginDestiny } from "./useLoginDestiny";
import DynamicTextField from "../Components/DynamicFields/DynamicTextField";
import emailRegex from "@presentation/regexes/emailRegex";
import { useNotificationModal } from "../Components/NotificationModal/useNotificationModal";
import NotificationModal from "../Components/NotificationModal";
import { SadFlower, Megaphone } from "../Components/Icons";

export interface ILoginWithPasswordFormValue {
  email: string;
  password: string;
}

export interface ILoginWithPasswordSlideProps {
  onSuccess: (sessionInfo: ISignInProcessInitWithUrlDto) => void;
  className?: string;
  setEmail: (email: string) => void;
  setPassword: (password: string) => void;
  setPendingConfirmation: (pendingConfirmation: boolean) => void;
}

const LoginWithPasswordSlide = (props: ILoginWithPasswordSlideProps) => {

  const [loading, setLoading] = useState(false);
  const [loadingCode, setLoadingCode] = useState(false);
  const { notificationModalProps, openNotificationModal } = useNotificationModal();
  const loginDestiny = useLoginDestiny();

  const onLoginFail = (statusMessage: string, email: string, password: string) => {

    if (statusMessage === "Email not confirmed") {
      openNotificationModal({
        closeLabel: "Cancelar",
        title: "Tu correo no ha sido confirmado aún.",
        actionLabel: "Enviar código",
        onAction: () => sendConfirmationCode(email, password),
        image: () => <Megaphone />,
        variant: "custom",
        description: `Por favor, oprime el botón "Enviar código" para que te llegue al correo que registraste y así confirmar tu cuenta. Una vez confirmado, podrás continuar con tu acceso al portal.`
      });
    }
    else {
      openNotificationModal({
        disableCloseButton: true,
        title: "No se logró iniciar sesión",
        actionLabel: "Aceptar",
        image: () => <SadFlower />,
        variant: "custom",
        description: `Verifica tu correo electrónico y contraseña.`
      });
    }

  };

  const onUnspectedError = () => {

  };

  const callApi = async (email: string, password: string) => {
    setLoading(true);

    const regex = new RegExp(emailRegex, "g");

    if (!regex.test(email)) {
      setLoading(false);
      openNotificationModal({
        disableCloseButton: true,
        title: "No se logró iniciar sesión",
        actionLabel: "Aceptar",
        image: () => <SadFlower />,
        variant: "custom",
        description: `Verifica tu correo electrónico`
      });
      return;
    }

    const response = await loginWithEmailAndPassword({
      email: email,
      password,
      destiny: loginDestiny,
    });

    if (!response.isSuccess) {
      setLoading(false);
      if (response.status === 401) {
        onLoginFail(response.body.message, email, password);
      } else {
        onUnspectedError();
      }
      return;
    }

    props.onSuccess(response.body);
  }

  const onSubmit = (value: ILoginWithPasswordFormValue, anyError: boolean) => {
    if (anyError) return;

    callApi(value.email, value.password);
  };

  const sendConfirmationCode = async (email: string, password: string) => {
    setLoadingCode(true);
    await sendVerificationCode({ email });
    props.setEmail(email);
    props.setPassword(password);
    props.setPendingConfirmation(true);
    setLoadingCode(false);
  }

  const formSchema = useMemo(() => {
    const schema: DynamicFormSchema<ILoginWithPasswordFormValue> = {
      submitLabel: "Iniciar sesión",
      submitFieldColumns: 12,
      initValues: {
        email: "",
        password: "",
      },
      columns: 12,
      fields: [
        {
          columns: 12,
          label: "Correo electrónico",
          name: "email",
          type: "string",
          attributes: {
            placeholder: "Ingresa tu correo",
            messageError: "Por favor completa este dato, debe tener el siguiente formato: ejemplo@gmail.com",
            regexes: [
              { expression: emailRegex },
              { expression: "^.{8,}$" },
            ],
            maxLength: 100,
            required: true,
          }
        },
        {
          columns: 12,
          label: "Contraseña",
          name: "password",
          type: "string",
          attributes: {
            placeholder: "Ingresa una contraseña",
            messageError: "Ingresa tu contraseña.",
            regexes: [{ expression: "^.{8,}$" }],
            maxLength: 20,
            type: "password",
            required: true,
          }
        },
      ],
      disableSubmitOnError: true,
    };
    return schema;
  }, []);

  const _className = useMemo(() => (
    [
      styles.root,
      props.className,
    ].filter(x => x !== undefined).join(" ")
  ), [props.className]);

  return (
    <div className={_className}>
      <DynamicForm
        className={styles.root}
        disabled={loading}
        loading={loading}
        onSubmit={onSubmit as any}
        schema={formSchema}
        context={{
          fieldsMap: {
            "string": DynamicTextField,
          },
          SubmitField: x => <LoginSubmitField loading={loading} {...x} label="Ingresar" />,
          requiredIndicator: true
        }}
      />
      <NotificationModal loading={loadingCode} {...notificationModalProps} />
    </div>
  );

};

export default LoginWithPasswordSlide;
